import React, { useEffect, useRef, useState } from "react";
import { InputText as PrimeInputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import { Slider } from "primereact/slider";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Paginator, PaginatorCurrentPageReportOptions, PaginatorRowsPerPageDropdownOptions } from "primereact/paginator";
import { Skeleton } from "primereact/skeleton";
import { Button } from "primereact/button";
import { Select, Spin } from "antd";
import { HeartIcon } from "@heroicons/react/24/outline";
import apiFunctions from "../../services/api-service";
import { AxiosError } from "axios";
import { errorHandler, formatDateTime } from "../../utils/HelperFunctions";
import "../../assets/css/datatable.css";

const { Option } = Select;

const CITY_ROWS = 30;

const BLOOD_GROUP_OPTIONS = [
    { label: "A+", value: 1 },
    { label: "A-", value: 2 },
    { label: "B+", value: 3 },
    { label: "B-", value: 4 },
    { label: "AB+", value: 5 },
    { label: "AB-", value: 6 },
    { label: "O+", value: 7 },
    { label: "O-", value: 8 },
];

// Full extents of the range sliders — a range is only sent to the API once
// the admin has actually narrowed it away from these bounds, so the default
// (untouched) view stays unfiltered.
const WEIGHT_MIN = 20;
const WEIGHT_MAX = 150;
const AGE_MIN = 1;
const AGE_MAX = 100;

const BloodGroupPage: React.FC = () => {
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    const [donors, setDonors] = useState<any[]>([]);
    const [dataLoad, setDataLoad] = useState(true);
    const [load, setLoad] = useState(true);
    const initialized = useRef(false);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    const [itemsPerPage, setItemsPerPage] = useState<any>(10);
    const [currentPage, setCurrentPage] = useState(0);
    const [totalRecords, setTotalRecords] = useState(0);
    const [totalPages, setTotalPages] = useState<any>(0);

    const [search, setSearch] = useState("");
    const [bloodGroupType, setBloodGroupType] = useState<number | null>(null);
    const [city, setCity] = useState<string | null>(null);

    // City filter — antd Select (showSearch + onPopupScroll), same
    // remote-search-with-infinite-scroll pattern used for Sub Cast/Surname/
    // Sub Admin pickers elsewhere in the admin (see RegistrationCreateEn.tsx).
    const [cityOptions, setCityOptions] = useState<string[]>([]);
    const [citySearchValue, setCitySearchValue] = useState("");
    const [cityPageIndex, setCityPageIndex] = useState(0);
    const [totalRecordsCity, setTotalRecordsCity] = useState(0);
    const [fetchingCity, setFetchingCity] = useState(false);

    // Committed (applied) ranges — drive the actual API call.
    const [weightRange, setWeightRange] = useState<[number, number]>([WEIGHT_MIN, WEIGHT_MAX]);
    const [ageRange, setAgeRange] = useState<[number, number]>([AGE_MIN, AGE_MAX]);
    // Live (dragging) values — only for the slider's own display, so every
    // pixel of drag doesn't fire a fresh API request; committed via onSlideEnd.
    const [weightDraft, setWeightDraft] = useState<[number, number]>([WEIGHT_MIN, WEIGHT_MAX]);
    const [ageDraft, setAgeDraft] = useState<[number, number]>([AGE_MIN, AGE_MAX]);
    // PrimeReact's Slider (a class component) calls onSlideEnd with whatever
    // closure was current when the drag STARTED, not the latest one — reading
    // through a ref (mutated on every onChange, dereferenced at call time)
    // sidesteps that staleness instead of racing it.
    const weightDraftRef = useRef<[number, number]>([WEIGHT_MIN, WEIGHT_MAX]);
    const ageDraftRef = useRef<[number, number]>([AGE_MIN, AGE_MAX]);

    const startIndex = currentPage * itemsPerPage;

    /* load a page of cities (reset=true replaces the list from page 0, otherwise appends the next page) */
    const fetchCityList = async (reset = false) => {
        if (fetchingCity) return;
        setFetchingCity(true);
        const pageToFetch = reset ? 0 : cityPageIndex;
        try {
            const params = new URLSearchParams({ page: String(pageToFetch), limit: String(CITY_ROWS) });
            if (citySearchValue.trim()) params.set("search", citySearchValue.trim());

            const response = await apiFunctions.get(`/bloodgroupadmin/cities?${params.toString()}`);
            const newItems: string[] = response?.data?.data || [];
            const total = response?.data?.totalRecords || 0;

            if (reset) {
                setCityOptions(newItems);
                setCityPageIndex(1);
            } else {
                setCityOptions((prev) => [...prev, ...newItems]);
                setCityPageIndex((prev) => prev + 1);
            }
            setTotalRecordsCity(total);
        } catch (err) {
            errorHandler(err as AxiosError);
        } finally {
            setFetchingCity(false);
        }
    };

    useEffect(() => {
        fetchCityList(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [citySearchValue]);

    const handleCityScroll = (e: any) => {
        const { scrollTop, offsetHeight, scrollHeight } = e.target;
        const nearBottom = scrollTop + offsetHeight >= scrollHeight - 10;
        const moreToLoad = cityOptions.length < totalRecordsCity;
        if (nearBottom && moreToLoad && !fetchingCity) fetchCityList();
    };

    const handleCitySearch = (value: string) => setCitySearchValue(value);

    async function getBloodGroupData() {
        try {
            setDataLoad(true);
            const params = new URLSearchParams({
                page: String(currentPage + 1),
                limit: String(itemsPerPage),
            });
            if (search.trim().length > 1) params.set("search", search.trim());
            if (bloodGroupType) params.set("bloodGroupType", String(bloodGroupType));
            if (city) params.set("city", city);
            if (weightRange[0] !== WEIGHT_MIN) params.set("minWeight", String(weightRange[0]));
            if (weightRange[1] !== WEIGHT_MAX) params.set("maxWeight", String(weightRange[1]));
            if (ageRange[0] !== AGE_MIN) params.set("minAge", String(ageRange[0]));
            if (ageRange[1] !== AGE_MAX) params.set("maxAge", String(ageRange[1]));

            await apiFunctions
                .get(`/bloodgroupadmin/list?${params.toString()}`)
                .then((response) => {
                    if (response && response.data && response.data.success) {
                        setTotalRecords(response.data.totalRecords || 0);
                        setTotalPages(response.data.totalPages || 0);
                        setDonors(response.data.data || []);
                    }
                })
                .catch((err: AxiosError) => {
                    errorHandler(err);
                })
                .finally(() => {
                    setDataLoad(false);
                    setLoad(false);
                });
        } catch (error) {
            console.log(error);
        }
    }

    const handlePageChange = (newPage: number) => setCurrentPage(newPage);

    const handleItemsPerPageChange = (value: number) => {
        setItemsPerPage(value);
        setCurrentPage(0);
    };

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearch(e.target.value);
        setCurrentPage(0);
    };

    const handleBloodGroupFilterChange = (value: number | null) => {
        setBloodGroupType(value);
        setCurrentPage(0);
    };

    const handleCityFilterChange = (value: string | null) => {
        setCity(value);
        setCurrentPage(0);
    };

    const commitWeightRange = (value: [number, number]) => {
        setWeightRange(value);
        setCurrentPage(0);
    };

    const commitAgeRange = (value: [number, number]) => {
        setAgeRange(value);
        setCurrentPage(0);
    };

    const resetFilters = () => {
        setSearch("");
        setBloodGroupType(null);
        setCity(null);
        setCitySearchValue("");
        setWeightRange([WEIGHT_MIN, WEIGHT_MAX]);
        setWeightDraft([WEIGHT_MIN, WEIGHT_MAX]);
        weightDraftRef.current = [WEIGHT_MIN, WEIGHT_MAX];
        setAgeRange([AGE_MIN, AGE_MAX]);
        setAgeDraft([AGE_MIN, AGE_MAX]);
        ageDraftRef.current = [AGE_MIN, AGE_MAX];
        setCurrentPage(0);
    };

    const hasActiveFilters =
        search.trim().length > 1 ||
        !!bloodGroupType ||
        !!city ||
        weightRange[0] !== WEIGHT_MIN ||
        weightRange[1] !== WEIGHT_MAX ||
        ageRange[0] !== AGE_MIN ||
        ageRange[1] !== AGE_MAX;

    useEffect(() => {
        if (!load) getBloodGroupData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage, itemsPerPage, search, bloodGroupType, city, weightRange, ageRange]);

    useEffect(() => {
        if (!initialized.current) {
            initialized.current = true;
            getBloodGroupData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const donorBodyTemplate = (row: any) => (
        row.isRegularDonor ? (
            <span
                className="px-2 py-1 rounded-full text-xs font-medium"
                style={{ background: "#dcfce7", color: "#15803d" }}
            >
                Regular Donor
            </span>
        ) : (
            <span className="text-xs" style={{ color: "#9ca3af" }}>—</span>
        )
    );

    const bloodGroupBodyTemplate = (row: any) => (
        <span
            className="px-2 py-1 rounded-full text-xs font-semibold"
            style={{ background: "#fee2e2", color: "#b91c1c" }}
        >
            {row.bloodGroupLabel || "-"}
        </span>
    );

    const header = (
        <div className="flex flex-col gap-3 table-header">
            <div className="grid grid-cols-12 gap-4 items-center">
                <div className="col-span-12 sm:col-span-3 flex flex-col gap-1">
                    <h2 className="card-title m-0 text-lg sm:text-xl">Blood Group Donors</h2>
                </div>
                <div className="col-span-12 sm:col-span-9 flex flex-col sm:flex-row gap-2 sm:justify-end sm:flex-wrap">
                    <div className="search-box flex items-center gap-2 w-full sm:w-auto">
                        <span className="text-gray-600">Search:</span>
                        <PrimeInputText
                            type="text"
                            value={search}
                            className="search-input w-full sm:w-auto"
                            placeholder="Search by name, mobile or city..."
                            onChange={handleSearchChange}
                        />
                    </div>
                    <Dropdown
                        value={bloodGroupType}
                        options={BLOOD_GROUP_OPTIONS}
                        onChange={(e) => handleBloodGroupFilterChange(e.value)}
                        placeholder="All Blood Groups"
                        showClear
                        className="w-full sm:w-auto"
                    />
                    <Select
                        style={{ height: 40, minWidth: 160 }}
                        className="w-full sm:w-auto"
                        popupMatchSelectWidth
                        showSearch
                        allowClear
                        placeholder="All Cities"
                        value={city || undefined}
                        onChange={(val) => handleCityFilterChange(val || null)}
                        filterOption={false}
                        onSearch={handleCitySearch}
                        onPopupScroll={handleCityScroll}
                        notFoundContent={fetchingCity ? <Spin size="small" /> : "No cities found"}
                    >
                        {cityOptions.map((c) => (
                            <Option key={c} value={c}>{c}</Option>
                        ))}
                    </Select>
                    {hasActiveFilters && (
                        <Button
                            label="Reset Filters"
                            text
                            severity="secondary"
                            onClick={resetFilters}
                            className="w-full sm:w-auto"
                        />
                    )}
                </div>
            </div>

            <div className="grid grid-cols-12 gap-6 items-center px-1">
                <div className="col-span-12 sm:col-span-6 flex flex-col gap-2">
                    <span className="text-sm text-gray-600">
                        Weight: <strong>{weightDraft[0]} — {weightDraft[1]} kg</strong>
                    </span>
                    <Slider
                        range
                        min={WEIGHT_MIN}
                        max={WEIGHT_MAX}
                        value={weightDraft}
                        onChange={(e) => {
                            const next = e.value as [number, number];
                            weightDraftRef.current = next;
                            setWeightDraft(next);
                        }}
                        onSlideEnd={() => commitWeightRange(weightDraftRef.current)}
                    />
                </div>
                <div className="col-span-12 sm:col-span-6 flex flex-col gap-2">
                    <span className="text-sm text-gray-600">
                        Age: <strong>{ageDraft[0]} — {ageDraft[1]} yrs</strong>
                    </span>
                    <Slider
                        range
                        min={AGE_MIN}
                        max={AGE_MAX}
                        value={ageDraft}
                        onChange={(e) => {
                            const next = e.value as [number, number];
                            ageDraftRef.current = next;
                            setAgeDraft(next);
                        }}
                        onSlideEnd={() => commitAgeRange(ageDraftRef.current)}
                    />
                </div>
            </div>
        </div>
    );

    const paginatoreTemplate = {
        layout: "RowsPerPageDropdown CurrentPageReport",
        RowsPerPageDropdown: (options: PaginatorRowsPerPageDropdownOptions) => {
            const dropdownOptions = [
                { label: 10, value: 10 },
                { label: 25, value: 25 },
                { label: 50, value: 50 },
                { label: 100, value: 100 },
            ];
            return (
                <div className="paginator-dropdown">
                    <span className="mx-1">Show</span>
                    <Dropdown
                        value={itemsPerPage}
                        options={dropdownOptions}
                        onChange={(e) => handleItemsPerPageChange(e.value)}
                    />
                </div>
            );
        },
        CurrentPageReport: (_options: PaginatorCurrentPageReportOptions) => (
            <span className="entries-text mx-1">entries</span>
        ),
    };

    return (
        <div className="dashboard-margin">
            {isMobile ? (
                <div className="bg-white w-full overflow-x-hidden box-border" style={{ borderRadius: "8px" }}>
                    <div className="p-3 pb-2">{header}</div>
                    <div className="flex flex-col gap-2 px-3 pb-3">
                        {dataLoad ? (
                            Array.from({ length: itemsPerPage }, (_, i) => (
                                <div key={i} className="border rounded-lg p-3 w-full box-border" style={{ borderColor: "#e5e7eb" }}>
                                    <Skeleton width="60%" height="1rem" className="mb-2" />
                                    <Skeleton width="40%" height="0.8rem" />
                                </div>
                            ))
                        ) : donors.length === 0 ? (
                            <div style={{ textAlign: "center", padding: "64px 0" }}>
                                <HeartIcon style={{ width: "48px", height: "48px", margin: "0 auto 12px", display: "block", color: "#d1d5db" }} />
                                <p style={{ margin: 0, fontSize: "14px", fontWeight: 500, color: "#6b7280" }}>No blood group donors found</p>
                            </div>
                        ) : (
                            donors.map((row: any) => (
                                <div key={row._id} className="border rounded-lg p-3 w-full box-border" style={{ borderColor: "#e5e7eb" }}>
                                    <div className="flex items-start justify-between gap-2 w-full">
                                        <p className="m-0 font-semibold text-sm break-words" style={{ color: "#264164" }}>{row.fullName || "-"}</p>
                                        {bloodGroupBodyTemplate(row)}
                                    </div>
                                    <div className="flex flex-col gap-1 mt-2 text-xs" style={{ color: "#4b5563" }}>
                                        <div><span className="font-medium">Mobile:</span> {row.mobileno || "-"}</div>
                                        {row.alternativeMobileNumber && (
                                            <div><span className="font-medium">Alt. Mobile:</span> {row.alternativeMobileNumber}</div>
                                        )}
                                        <div><span className="font-medium">Weight:</span> {row.weight || "-"} kg &nbsp;·&nbsp; <span className="font-medium">Age:</span> {row.age ?? "-"}</div>
                                        <div><span className="font-medium">City:</span> {row.currentcity || "-"}</div>
                                        <div><span className="font-medium">Joined:</span> {formatDateTime(row.createddate)}</div>
                                    </div>
                                    <div className="mt-2">{donorBodyTemplate(row)}</div>
                                </div>
                            ))
                        )}
                    </div>
                </div>
            ) : (
                <DataTable
                    scrollable
                    scrollHeight="calc(100vh - 260px)"
                    style={{ minHeight: "calc(100vh - 185px)", background: "#fff" }}
                    header={header}
                    value={
                        dataLoad
                            ? Array.from({ length: itemsPerPage }, (_, i) => ({ _skeletonId: i }))
                            : donors
                    }
                    dataKey={dataLoad ? "_skeletonId" : "_id"}
                    resizableColumns
                    rows={itemsPerPage}
                    emptyMessage={
                        <div style={{ textAlign: "center", padding: "64px 0" }}>
                            <HeartIcon style={{ width: "48px", height: "48px", margin: "0 auto 12px", display: "block", color: "#d1d5db" }} />
                            <p style={{ margin: 0, fontSize: "14px", fontWeight: 500, color: "#6b7280" }}>No blood group donors found</p>
                        </div>
                    }
                >
                    <Column
                        field="fullName"
                        header="Name"
                        body={(row) => (dataLoad ? <Skeleton /> : row.fullName || "-")}
                    />
                    <Column
                        field="bloodGroupLabel"
                        header="Blood Group"
                        body={(row) => (dataLoad ? <Skeleton /> : bloodGroupBodyTemplate(row))}
                    />
                    <Column
                        field="mobileno"
                        header="Mobile Number"
                        body={(row) => (dataLoad ? <Skeleton /> : row.mobileno || "-")}
                    />
                    <Column
                        field="alternativeMobileNumber"
                        header="Alternative Mobile"
                        body={(row) => (dataLoad ? <Skeleton /> : row.alternativeMobileNumber || "-")}
                    />
                    <Column
                        field="weight"
                        header="Weight (kg)"
                        body={(row) => (dataLoad ? <Skeleton /> : row.weight || "-")}
                    />
                    <Column
                        field="age"
                        header="Age"
                        body={(row) => (dataLoad ? <Skeleton /> : row.age ?? "-")}
                    />
                    <Column
                        field="isRegularDonor"
                        header="Donor Status"
                        body={(row) => (dataLoad ? <Skeleton /> : donorBodyTemplate(row))}
                    />
                    <Column
                        field="currentcity"
                        header="City"
                        body={(row) => (dataLoad ? <Skeleton /> : row.currentcity || "-")}
                    />
                    <Column
                        field="createddate"
                        header="Joined"
                        body={(row) => (dataLoad ? <Skeleton /> : formatDateTime(row.createddate))}
                    />
                </DataTable>
            )}

            {totalRecords > 0 && (
                <div className="flex flex-col sm:flex-row items-center sm:justify-between gap-2 bg-white p-2 mt-2 sm:mt-0" style={{ borderRadius: "8px" }}>
                    <Paginator
                        template={paginatoreTemplate}
                        first={startIndex}
                        rows={itemsPerPage}
                        totalRecords={totalRecords}
                    />
                    <div className="flex items-center gap-2 w-full sm:w-auto">
                        <Button
                            onClick={() => handlePageChange(currentPage - 1)}
                            className="pagination-button flex-shrink-0 px-2 sm:px-4"
                            disabled={currentPage === 0}
                        >
                            <span className="sm:hidden">Prev</span>
                            <span className="hidden sm:inline">Previous</span>
                        </Button>
                        <Button className="pagination-button flex-1 min-w-0 cursor-default px-1 sm:px-4">
                            <span className="pagination-text whitespace-nowrap text-xs sm:text-sm">
                                Page {currentPage + 1} of {totalPages || 1}
                            </span>
                        </Button>
                        <Button
                            onClick={() => handlePageChange(currentPage + 1)}
                            className="pagination-button flex-shrink-0 px-2 sm:px-4"
                            disabled={currentPage + 1 === (totalPages === 0 ? 1 : totalPages)}
                        >
                            Next
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default BloodGroupPage;
